import React, { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import config from "./config";

// Create the PermissionContext
const PermissionContext = createContext();

// Default permissions (all false) for fallback
const defaultPermissions = {
  add_department: 0,
  view_doctors_list_own: 0,
  view_doctors_list_all: 0,
  add_doctor: 0,
  add_archive_doctor: 0,
  view_archive_doctors_list: 0,
  reactivate_archive_doctor: 0,
  view_admin_users_own: 0,
  view_admin_users_all: 0,
  add_admin_users: 0,
  view_appointments_own: 0,
  view_appointments_all: 0,
  add_appointment: 0,
};

// PermissionProvider Component
export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(null);
  const [businessId, setBusinessId] = useState(localStorage.getItem("BusinessID"));
  const userId = localStorage.getItem("userUUID");

  // Fetch permissions when businessId or userId changes
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!businessId || !userId) {
        console.error("Cannot fetch permissions: Missing BusinessID or userUUID", {
          businessId,
          userId,
        });
        setPermissions(defaultPermissions);
        return;
      }

      try {
        const response = await fetch(`${config.API_URL}get_admins_permissions.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            business_id: businessId,
            user_id: userId,
          }),
        });

        if (!response.ok) {
          throw new Error(`Permissions fetch failed with status: ${response.status}`);
        }

        const data = await response.json();

        if (data.status === "success") {
          setPermissions(data.permissions);
          console.log("Permissions fetched:", data.permissions);
        } else {
          console.error("Permissions API error:", data.message);
          setPermissions(defaultPermissions);
        }
      } catch (error) {
        console.error("Error fetching permissions:", error.message);
        setPermissions(defaultPermissions);
      }
    };

    fetchPermissions();
  }, [businessId, userId]);

  // Function to update businessId (called when switching businesses)
  const updateBusinessId = (newBusinessId) => {
    localStorage.setItem("BusinessID", newBusinessId);
    setBusinessId(newBusinessId);
  };

  return (
    <PermissionContext.Provider value={{ permissions, businessId, updateBusinessId }}>
      {children}
    </PermissionContext.Provider>
  );
};

// PropTypes validation for PermissionProvider
PermissionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Custom hook to use permissions
export const usePermission = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermission must be used within a PermissionProvider");
  }
  return context;
};